import { createLogger } from '@/lib/logger';

const logger = createLogger('hooks/mutations/useMemberMutations');

/**
 * Member mutation hooks using factory pattern with specialized operations
 * Reduced from 127 lines to ~90 lines (29% reduction)
 * All features preserved: update, permissions, invites
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { memberApi } from '@/lib/api/members.api';
import { permissionsApi } from '@/lib/api/permissions.api';
import { queryKeys } from '../query-keys';
import { createResourceMutations } from './createResourceMutations';
import { toast } from 'sonner';
import { useAccountType } from '@/hooks/useAccountType';
import type { CreateMember, Member, UpdateMember } from '@scrambl/shared';
// Core update mutation using factory
const memberMutations = createResourceMutations<Member, never, UpdateMember>({
  resourceName: 'Member',
  resourceNameSingular: 'member',
  queryKeyFactory: queryKeys.members,
  api: {
    create: () => { throw new Error('Member create not supported via mutations'); },
    update: (id: string, data: UpdateMember) => memberApi.updateMember(id, data),
    updateField: (id: string, field: string, value: unknown) => {
      const updateData = { [field]: value } as UpdateMember;
return memberApi.updateMember(id, updateData);
    },
    delete: () => { throw new Error('Member delete not supported via mutations'); },
  },
  invalidation: {
    onUpdate: {
      additionalInvalidations: [
        () => queryKeys.members.display(),
        () => queryKeys.teams.all,
      ],
    },
  },
  enableRealtime: false,
});

export const useUpdateMemberMutation = memberMutations.useUpdateMutation;
export const useUpdateMemberFieldMutation = memberMutations.useUpdateFieldMutation!;

export function useCreateMemberMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMember) => memberApi.createMember(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.members.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.members.display() });
      queryClient.invalidateQueries({ queryKey: queryKeys.members.count() });
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
      toast.success('Member created');
    },
    onError: (error: Error) => {
      logger.error('[useCreateMemberMutation] Error:', error);
      toast.error(error.message || 'Failed to create member');
    },
  });
}

/**
 * Mutation hook to update a member's permissions
 */
export function useUpdateMemberPermissionsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, permissionSetIds }: { id: string; permissionSetIds: string[] }) =>
      permissionsApi.updateMemberPermissions(id, permissionSetIds),
    onSuccess: (_, variables) => {
      // Update the specific member in cache with new permissions
      queryClient.setQueryData(
        queryKeys.members.detail(variables.id),
        (old: Member | undefined) => {
          if (!old) return old;
          return {
            ...old,
            permissions: {
              permissionSets: variables.permissionSetIds.map(permissionSetId => ({
                id: crypto.randomUUID(),
                memberId: variables.id,
                permissionSetId,
                createdAt: new Date()
              }))
            }
          };
        }
      );
      
      // Invalidate member detail and lists to refetch fresh data
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.members.detail(variables.id) 
      });
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.members.lists() 
      });
      
      toast.success('Member permissions updated successfully');
    },
    onError: (error) => {
      logger.error('[useUpdateMemberPermissionsMutation] Error:', error);
      toast.error('Failed to update member permissions');
    },
  });
}

/**
 * Mutation hook to remove a member from the organization (admin action)
 */
export function useRemoveMemberMutation() {
  const queryClient = useQueryClient();
  const { isPersonal } = useAccountType();

  return useMutation({
    mutationFn: (memberId: string) => memberApi.removeMember(memberId),
    onSuccess: () => {
      // Invalidate all member-related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.members.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.members.count() });
      toast.success(
        isPersonal ? 'Member removed from space' : 'Member removed from organization',
      );
    },
    onError: (error: Error) => {
      logger.error('[useRemoveMemberMutation] Error:', error);
      toast.error(error.message || 'Failed to remove member');
    },
  });
}

/**
 * Mutation hook to invite members to an organization
 */
export function useInviteMembersMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organizationId, emails, role, permissionSetId }: {
      organizationId: string;
      emails: string[];
      role: string;
      permissionSetId?: string;
    }) =>
      memberApi.inviteMembers(organizationId, {
        emails,
        role,
        permissionSetId,
      }),
    onSuccess: () => {
      // Invalidate member lists to show newly invited members
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.members.lists() 
      });
      
      // Also invalidate member count
      queryClient.invalidateQueries({
        queryKey: queryKeys.members.count(),
      });
      
      toast.success('Invitations sent successfully');
    },
    onError: (error) => {
      logger.error('[useInviteMembersMutation] Error:', error);
      toast.error('Failed to send invitations');
    },
  });
}
